import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ListingBadge, daysOnMarket } from "@/components/ListingBadge";
import { PostcardButton } from "@/components/PostcardButton";
import { MailBadge } from "@/components/MailBadge";
import { ConsentDialog } from "@/components/ConsentDialog";
import { EditLeadButton, DeleteLeadButton } from "@/components/LeadEditDelete";
import { AssignLead } from "@/components/AssignLead";
import { PipelineStageSelect } from "@/components/PipelineStage";
import { AstoriaDetails } from "@/components/AstoriaDetails";
import { MarketFieldList } from "@/components/MarketLeadEditor";
import { useAuth } from "@/context/AuthContext";
import {
  Mail, Voicemail, HeartHandshake, Ban, Play, Pause, Send, StickyNote,
  Loader2, MapPin, Home, TrendingUp, TrendingDown, ShieldAlert, PhoneCall, MessageSquareText, RefreshCw,
} from "lucide-react";

const money = (n) => (n || n === 0) && !isNaN(n) ? `$${Number(n).toLocaleString()}` : (n || "—");
const canAiCall = (p) => p?.source === "astoria" ? !!p.astoria_ai_call_ok : (["import", "inbound"].includes(p?.source) || p?.status === "Interested" || p?.interest_status === "Interested");

const COMM_ICON = {
  email: Mail, voicemail_prospect: Voicemail, voicemail_mlo_duplicate: Voicemail,
  note: StickyNote, status_change: TrendingUp, response: Mail, postcard: Send, ai_call: PhoneCall,
};

export default function ProspectDrawer({ prospect, onClose, onChange, release }) {
  const [p, setP] = useState(prospect);
  const [comms, setComms] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const [callConsent, setCallConsent] = useState(false);
  const [playing, setPlaying] = useState(null);
  const [audio, setAudio] = useState(null);
  const [price, setPrice] = useState(0);
  const { user } = useAuth();

  useEffect(() => { api.get("/config").then((r) => setPrice(Number(r.data.individual_contact_price) >= 0.5 ? r.data.individual_contact_price : 0)).catch(() => {}); }, []);

  useEffect(() => {
    setP(prospect);
    if (prospect) {
      api.get(`/prospects/${prospect.id}/communications`).then((r) => setComms(r.data));
    }
  }, [prospect]);

  if (!prospect) return null;

  const act = async (fn, label) => {
    setBusy(label);
    try {
      await fn();
      const fresh = await api.get(`/prospects/${prospect.id}`);
      setP(fresh.data);
      const c = await api.get(`/prospects/${prospect.id}/communications`);
      setComms(c.data);
      onChange?.();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const sendEmail = () => act(async () => { await api.post(`/prospects/${prospect.id}/email`); toast.success("Email composed & recorded"); }, "email");
  const aiCall = () => act(async () => {
    const { data } = await api.post("/payments/contact-checkout", { prospect_id: prospect.id, action: "ai_call", origin_url: window.location.origin, consent: true });
    if (data.checkout_url) { window.location.href = data.checkout_url; return; }
    toast.success("AI call started — see the AI Call Log for the outcome");
  }, "aicall");
  const markInterested = () => act(async () => { await api.post(`/prospects/${prospect.id}/mark-interested`); toast.success("Moved to Interested Leads"); }, "interested");
  const markDnc = () => act(async () => { await api.post(`/prospects/${prospect.id}/dnc`); toast.success("Added to Do Not Contact"); }, "dnc");
  const addNote = () => act(async () => { await api.post(`/prospects/${prospect.id}/note`, { note }); setNote(""); toast.success("Note added"); }, "note");

  const togglePlay = (url, id) => {
    if (!url) { toast.error("No audio available yet for this recording"); return; }
    if (playing === id) { audio?.pause(); setPlaying(null); return; }
    audio?.pause();
    const a = new Audio(url);
    a.onended = () => setPlaying(null);
    a.play(); setAudio(a); setPlaying(id);
  };

  const Fin = ({ label, value, est }) => (
    <div className="rounded-lg border border-slate-100 bg-slate-50/60 p-3">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}{est && " (est.)"}</div>
      <div className="mt-0.5 font-semibold text-slate-900">{value}</div>
    </div>
  );

  return (
    <Sheet open={!!prospect} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="prospect-drawer">
        <SheetHeader>
          <SheetTitle className="font-display text-xl">{p?.name || "Prospect"}</SheetTitle>
        </SheetHeader>

        <div className="mt-2 flex flex-wrap gap-2">
          <Badge className="bg-amber-100 text-amber-800 border-0">{p?.opportunity_type || "—"}</Badge>
          <Badge variant="outline">{p?.status}</Badge>
          {p?.estimated_fields_marked && <Badge variant="outline" className="border-slate-300 text-slate-500">Estimated data</Badge>}
          {p?.skip_traced && <Badge variant="outline" className="border-sky-300 text-sky-700" data-testid="skip-traced-badge">Skip-traced contact</Badge>}
          {p?.recycled && <Badge variant="outline" className="border-teal-300 text-teal-700" data-testid="recycled-badge">Recycled</Badge>}
          {p?.source === "inbound" && <Badge variant="outline" className="border-emerald-300 text-emerald-700" data-testid="inbound-badge">Inbound call</Badge>}
          {p?.source === "astoria" && <Badge variant="outline" className="border-indigo-300 text-indigo-700" data-testid="astoria-badge">Astoria / MortgageLeads.com</Badge>}
          <ListingBadge p={p} testId="drawer-listing-badge" />
          <MailBadge p={p} testId="drawer-mail-badge" />
        </div>
        {p && (
          <div className="mt-3 flex gap-2">
            <EditLeadButton p={p} onSaved={() => act(async () => {}, "edit")} />
            <DeleteLeadButton p={p} onDeleted={() => { onChange?.(); onClose(); }} />
          </div>
        )}
        {p && <PipelineStageSelect p={p} onChanged={() => act(async () => {}, "stage")} />}
        {p && user?.role === "admin" && <AssignLead p={p} onAssigned={() => act(async () => {}, "assign")} />}
        {p?.exclusivity_note && <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="exclusivity-note">{p.exclusivity_note}</div>}
        {p?.attom_verified === "verified" && (
          <div className="mt-3 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900" data-testid="attom-verified-note">
            <div className="font-semibold">Property data verified with ATTOM</div>
            {(p.attom_corrections || []).length ? p.attom_corrections.map((c) => <div key={c}>Corrected {c}</div>) : <div>No corrections needed.</div>}
          </div>
        )}

        {(() => {
          if (!release?.enabled || !release?.days || !p || p.last_engaged_at) return null;
          if (["Interested", "Converted", "Do Not Contact", "Not Interested", "Released"].includes(p.status)) return null;
          if (!p.created_at) return null;
          const d = Math.ceil(release.days - (Date.now() - new Date(p.created_at).getTime()) / 86400000);
          return <div className={`mt-3 rounded-lg border px-3 py-2 text-xs ${d <= 1 ? "border-red-200 bg-red-50 text-red-700" : "border-amber-200 bg-amber-50 text-amber-800"}`} data-testid="expiry-warning">{d <= 0 ? "This lead is being released to the team — contact it now to keep it." : `Contact this lead within ${d} day${d === 1 ? "" : "s"} or it will be released to the team.`}</div>;
        })()}

        {/* Actions */}
        <div className="mt-5 grid grid-cols-2 gap-2">
          <Button onClick={sendEmail} disabled={busy} data-testid="drawer-email-btn" className="bg-indigo-600 hover:bg-indigo-700">
            {busy === "email" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="mr-1.5 h-4 w-4" /> {p?.email_status === "Email Sent" ? "Resend Email" : "Send Email"}</>}
          </Button>
          <Button onClick={markInterested} disabled={busy} data-testid="drawer-interested-btn" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
            <HeartHandshake className="mr-1.5 h-4 w-4" /> Interested
          </Button>
          <Button onClick={markDnc} disabled={busy} data-testid="drawer-dnc-btn" variant="outline" className="border-red-200 text-red-700 hover:bg-red-50">
            <Ban className="mr-1.5 h-4 w-4" /> Do Not Contact
          </Button>
          <Button onClick={() => setCallConsent(true)} disabled={!!busy || !canAiCall(p)} title={canAiCall(p) ? "" : "AI calls are only for imported leads, inbound callers and prospects marked Interested"} data-testid="drawer-ai-call-btn" className="bg-teal-700 hover:bg-teal-800">
            {busy === "aicall" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><PhoneCall className="mr-1.5 h-4 w-4" /> AI Call{price ? ` · $${price}` : ""}</>}
          </Button>
          {!canAiCall(p) && <p className="col-span-2 text-xs text-slate-500" data-testid="ai-call-locked-note">{p?.source === "astoria" ? "AI calling is off for this lead because its MortgageLeads.com consent record doesn't allow phone or automated calls." : "AI calling unlocks once this prospect is marked Interested (imported and inbound leads can be called anytime)."}</p>}
          <PostcardButton prospect={p} price={price} disabled={!!busy} onSent={() => act(async () => {}, "postcard")} />
          <Button variant="outline" disabled={!p?.phone} onClick={() => { window.location.href = `${window.location.pathname.startsWith("/admin") ? "/admin/texts" : "/texts"}?p=${p.id}`; }} data-testid="drawer-text-btn">
            <MessageSquareText className="mr-1.5 h-4 w-4" /> Text
          </Button>
          <Button variant="outline" disabled={!!busy} title="Free - re-checks property value, mortgage, for-sale status and owner details for this lead" data-testid="drawer-refresh-data-btn"
            onClick={() => act(async () => { await api.post(`/prospects/${p.id}/refresh-data`); toast.success("Lead data updated"); }, "refresh")}>
            {busy === "refresh" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />} Update data (free)
          </Button>
          <ConsentDialog open={callConsent} onOpenChange={setCallConsent} onConfirm={aiCall} testId="ai-call-consent"
            title="Consent required before AI call" confirmLabel={price ? `Continue to payment · $${price}` : "Start AI call"}
            description="Calls using an AI / artificial voice are regulated by the TCPA and state telemarketing laws. If the prospect wants to talk, the AI warm-transfers the call to the phone in your profile."
            statement={`I confirm I have the consent required under the TCPA and applicable state law to call ${p?.name || "this person"} using an AI / artificial voice, and for the AI to leave a voicemail if they don't answer.`} />
        </div>

        {/* Contact & property */}
        <div className="mt-6 space-y-3 text-sm">
          <div className="flex items-center gap-2 text-slate-600"><MapPin className="h-4 w-4 text-slate-400" />{[p?.property_address, p?.city, p?.state, p?.zip_code].filter(Boolean).join(", ") || "No address"}</div>
          <div className="flex flex-wrap gap-4 text-slate-600">
            <span>{p?.email || "No email"}</span><span>{p?.phone || "No phone"}</span>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2">
          {Number(p?.listing_price) > 0 && <>
            <Fin label="Asking price" value={money(p.listing_price)} />
            <Fin label="Days on market" value={daysOnMarket(p) ?? "—"} />
          </>}
          <Fin label="Property value" value={money(p?.estimated_property_value)} est />
          <Fin label="Mortgage balance" value={money(p?.estimated_mortgage_balance)} est />
          <Fin label="Equity amount" value={money(p?.estimated_equity_amount)} est />
          <Fin label="Equity %" value={p?.estimated_equity_percentage ? `${p.estimated_equity_percentage}%` : "—"} est />
          <Fin label="LTV" value={p?.estimated_ltv ? `${p.estimated_ltv}%` : "—"} est />
          <Fin label="Est. existing rate" value={p?.estimated_current_rate ? `${p.estimated_current_rate}%${p.rate_is_inferred === false ? " (recorded)" : ""}` : "—"} est={p?.rate_is_inferred !== false} />
          {p?.rate_type && <Fin label="Loan rate type" value={p.rate_type === "ARM" ? "Adjustable (ARM)" : "Fixed"} />}
          {p?.mortgage_origination_date && <Fin label="Latest loan (purchase or refi)" value={p.mortgage_origination_date} />}
          <Fin label="Today's market rate" value={p?.market_rate ? `${p.market_rate}%` : "—"} />
          <Fin label="Homeowner age" value={p?.homeowner_age || "—"} />
          <Fin label="Home on the market" value={p?.on_market === true ? "Yes - listed for sale" : p?.on_market === false ? "No" : "—"} />
          {p?.permits_checked && (p.permit_ages?.length
            ? p.permit_ages.map((x) => <Fin key={x.kind} label={`${x.kind} permit`} value={`${x.date.slice(0, 4)} (${x.age_years} yr${x.age_years === 1 ? "" : "s"} old)${x.age_years >= 10 ? " · 10+ yrs" : ""}`} />)
            : <Fin label="Permits" value="None on record" />)}
        </div>
        {p?.enrichment_note && <p className="mt-2 text-xs text-amber-700" data-testid="drawer-enrichment-note">{p.enrichment_note}</p>}
        <AstoriaDetails p={p} />
        {p?.source === "marketplace" && <div className="mt-4 rounded-lg border border-slate-200 p-4"><div className="text-sm font-semibold text-slate-900">Marketplace lead details</div><MarketFieldList src={p} testId="drawer-market-fields" /></div>}

        {p?.opportunity_type === "Refinance" && p?.estimated_current_rate && p?.market_rate && (
          <div className="mt-3 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900" data-testid="refi-rate-gap">
            <TrendingDown className="h-4 w-4 text-emerald-600" />
            Inferred rate is <b>{(Number(p.estimated_current_rate) - Number(p.market_rate)).toFixed(2)}%</b> above today's market rate — refinance opportunity.
          </div>
        )}

        {(p?.qualification_evidence || p?.qualification_explanation) && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm">
            <div className="mb-1 flex items-center gap-1.5 font-semibold text-amber-900"><ShieldAlert className="h-4 w-4" /> Qualification</div>
            {p.qualification_explanation && <p className="text-amber-900/90">{p.qualification_explanation}</p>}
            {p.qualification_evidence && <p className="mt-1 text-xs text-amber-800/80">{p.qualification_evidence}</p>}
          </div>
        )}

        {/* Notes */}
        <div className="mt-6">
          <div className="mb-2 text-sm font-semibold text-slate-700">Add note</div>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="note-input" placeholder="Log a call, follow-up or detail…" rows={2} />
          <Button size="sm" className="mt-2" disabled={!note || busy} onClick={addNote} data-testid="add-note-btn">
            <StickyNote className="mr-1.5 h-4 w-4" /> Save note
          </Button>
        </div>

        {/* Timeline */}
        <div className="mt-6">
          <div className="mb-3 text-sm font-semibold text-slate-700">Communication History</div>
          <div className="space-y-3">
            {comms.length === 0 && <p className="text-sm text-slate-400">No activity yet.</p>}
            {comms.map((c) => {
              const Icon = COMM_ICON[c.type] || StickyNote;
              const isVoicemail = c.type?.startsWith("voicemail");
              return (
                <div key={c.id} className="flex gap-3 rounded-lg border border-slate-100 p-3" data-testid={`comm-${c.id}`}>
                  <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-600"><Icon className="h-4 w-4" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold capitalize text-slate-800">{c.type.replace(/_/g, " ")}</span>
                      <span className="text-[11px] text-slate-400">{new Date(c.created_at).toLocaleString()}</span>
                    </div>
                    {c.subject && <div className="text-sm text-slate-700">{c.subject}</div>}
                    {c.body && <p className="mt-1 whitespace-pre-wrap text-xs text-slate-500 line-clamp-4">{c.body}</p>}
                    {c.detail && <p className="text-xs text-slate-500">{c.detail}</p>}
                    {c.type !== "ai_call" && c.status && <Badge variant="outline" className="mt-1 text-[10px]">{c.status}</Badge>}
                    {c.type === "ai_call" && <Badge variant="outline" className="mt-1 text-[10px]" data-testid="ai-call-comm-status">{c.status}</Badge>}
                    {c.type === "ai_call" && c.recording_url && <audio controls preload="none" src={c.recording_url} className="mt-2 h-9 w-full" data-testid={`ai-call-recording-${c.id}`} />}
                    {isVoicemail && (
                      <Button size="sm" variant="outline" data-testid="voicemail-play-btn" className="mt-2 h-8"
                        onClick={() => togglePlay(c.delivered_recording_url || c.recording_url, c.id)}>
                        {playing === c.id ? <Pause className="mr-1 h-3.5 w-3.5" /> : <Play className="mr-1 h-3.5 w-3.5" />}
                        Play Voicemail
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
